/* eslint-disable no-unused-vars */
import { useEffect, useState, useCallback, useRef } from 'react';
import socketService from '../services/socketService';

// =====================
// Types
// =====================

export interface BulkUploadResult {
  success: boolean;
  data?: any[];
  duplicates?: Array<{
    entity: any;
    message: string;
    existing_entity_id: string;
    existing_database_entity_id: string;
  }>;
  duplicatesRemoved?: number;
  databaseId?: string;
  message?: string;
  jobId?: string;
}

export interface BulkUploadFailedResult {
  jobId: string;
  error: string;
  databaseId?: string;
}

export interface BulkUploadCallbacks {
  onComplete?: (result: BulkUploadResult) => void;
  onFailed?: (result: BulkUploadFailedResult) => void;
}

// =====================
// Main Hook: useSocketIO
// =====================

/**
 * Hook to manage Socket.IO connection and event subscriptions.
 * Provides low-level access to subscribe/unsubscribe from events.
 *
 * @returns {object} Socket connection state and event handlers
 *
 * @example
 * const { isConnected, subscribe, unsubscribe } = useSocketIO();
 *
 * useEffect(() => {
 *   const handler = (data) => console.log(data);
 *   subscribe('my-event', handler);
 *   return () => unsubscribe('my-event', handler);
 * }, [subscribe, unsubscribe]);
 */
export const useSocketIO = (): {
  isConnected: boolean;
  subscribe: <T = any>(event: string, callback: (data: T) => void) => void;
  unsubscribe: <T = any>(event: string, callback: (data: T) => void) => void;
  socket: ReturnType<typeof socketService.getSocket>;
} => {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<ReturnType<typeof socketService.getSocket>>(null);

  useEffect(() => {
    // Connect to socket
    const socket = socketService.connect();
    socketRef.current = socket;

    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => setIsConnected(false);

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // Set initial connection state
    setIsConnected(socket.connected);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, []);

  const subscribe = useCallback(<T = any>(event: string, callback: (_data: T) => void) => {
    socketRef.current?.on(event, callback);
  }, []);

  const unsubscribe = useCallback(<T = any>(event: string, callback: (_data: T) => void) => {
    socketRef.current?.off(event, callback);
  }, []);

  return {
    isConnected: isConnected,
    subscribe: subscribe,
    unsubscribe: unsubscribe,
    socket: socketRef.current,
  };
};

// =====================
// Higher-Level Hook: useBulkUploadEvents
// =====================

/**
 * Hook specifically for listening to bulk upload events.
 * Handles both job-specific and general bulk upload events.
 *
 * @param {string} [jobId] - Optional job ID to listen for specific job events
 * @param {BulkUploadCallbacks} [callbacks] - Callback functions for complete/failed events
 * @returns {object} Connection state and last event results
 *
 * @example
 * // Listen for a specific job
 * useBulkUploadEvents(jobId, {
 *   onComplete: (result) => {
 *     console.log('Upload complete:', result);
 *     refetchData();
 *   },
 *   onFailed: (result) => {
 *     console.error('Upload failed:', result.error);
 *   }
 * });
 *
 * @example
 * // Listen for all bulk upload events (e.g., for a dashboard)
 * useBulkUploadEvents(undefined, {
 *   onComplete: (result) => {
 *     if (result.databaseId === currentDatabaseId) {
 *       refetchData();
 *     }
 *   }
 * });
 */
export const useBulkUploadEvents = (
  jobId?: string,
  callbacks?: BulkUploadCallbacks,
): {
  isConnected: boolean;
  lastResult: BulkUploadResult | null;
  lastError: BulkUploadFailedResult | null;
} => {
  const { isConnected, subscribe, unsubscribe } = useSocketIO();
  const [lastResult, setLastResult] = useState<BulkUploadResult | null>(null);
  const [lastError, setLastError] = useState<BulkUploadFailedResult | null>(null);

  useEffect(() => {
    let cleanup: (() => void) | undefined = undefined;

    if (isConnected) {
      // Handler for complete events
      const handleComplete = (result: BulkUploadResult) => {
        setLastResult(result);
        callbacks?.onComplete?.(result);
      };

      // Handler for failed events
      const handleFailed = (result: BulkUploadFailedResult) => {
        setLastError(result);
        callbacks?.onFailed?.(result);
      };

      if (jobId) {
        subscribe(`bulk-upload-complete-${jobId}`, handleComplete);
        subscribe(`bulk-upload-failed-${jobId}`, handleFailed);
      } else {
        subscribe('bulk-upload-complete', handleComplete);
        subscribe('bulk-upload-failed', handleFailed);
      }

      cleanup = () => {
        if (jobId) {
          unsubscribe(`bulk-upload-complete-${jobId}`, handleComplete);
          unsubscribe(`bulk-upload-failed-${jobId}`, handleFailed);
        } else {
          unsubscribe('bulk-upload-complete', handleComplete);
          unsubscribe('bulk-upload-failed', handleFailed);
        }
      };
    }

    return cleanup;
  }, [isConnected, jobId, subscribe, unsubscribe, callbacks]);

  return {
    isConnected: isConnected,
    lastResult: lastResult,
    lastError: lastError,
  };
};

export default useSocketIO;
